import { Candidate } from "@/types/candidate";

export type DuplicateReason = "email" | "phone" | "linkedin";

export interface DuplicateGroup {
	key: string;
	candidates: Candidate[];
	reasons: DuplicateReason[];
}

const LINKEDIN_PROFILE_PREFIX = /https?:\/\/(www\.)?linkedin\.com\/(in|pub)\//;

// Normalization rules mirror useDuplicateCandidates so the finder and the
// candidate detail banner agree on what a duplicate is.
export const normalizeEmail = (email?: string | null): string | null =>
	email?.trim().toLowerCase() || null;

export const normalizePhone = (phone?: string | null): string | null =>
	phone?.replace(/[\s\-\(\)\+]/g, "") || null;

export const normalizeLinkedIn = (url?: string | null): string | null => {
	const trimmedUrl = url?.trim().toLowerCase();
	if (!trimmedUrl || trimmedUrl.length < 10)
		return null;
	if (!trimmedUrl.includes("linkedin.com"))
		return null;
	if (!trimmedUrl.includes("/in/") && !trimmedUrl.includes("/pub/"))
		return null;

	const profileId = trimmedUrl
		.replace(LINKEDIN_PROFILE_PREFIX, "")
		.replace(/\/$/, "")
		.split("?")[0];

	return profileId.length >= 3 ? profileId : null;
};

/**
 * Groups candidates sharing an email, phone or LinkedIn profile.
 * Matches are transitive: if A matches B on email and B matches C on phone,
 * A, B and C end up in the same group.
 */
export function groupDuplicateCandidates(candidates: Candidate[]): DuplicateGroup[] {
	const parent = candidates.map((_, index) => index);
	const find = (index: number): number => {
		while (parent[index] !== index) {
			parent[index] = parent[parent[index]];
			index = parent[index];
		}
		return index;
	};

	const firstIndexByKey = new Map<string, number>();
	const matches: Array<[number, DuplicateReason]> = [];

	candidates.forEach((candidate, index) => {
		const keys: Array<[DuplicateReason, string | null]> = [
			["email", normalizeEmail(candidate.email)],
			["phone", normalizePhone(candidate.phone)],
			["linkedin", normalizeLinkedIn(candidate.linkedin)],
		];

		for (const [reason, value] of keys) {
			if (!value)
				continue;

			const key = `${reason}:${value}`;
			const firstIndex = firstIndexByKey.get(key);
			if (firstIndex === undefined) {
				firstIndexByKey.set(key, index);
				continue;
			}

			parent[find(index)] = find(firstIndex);
			matches.push([index, reason]);
		}
	});

	const reasonsByRoot = new Map<number, Set<DuplicateReason>>();
	for (const [index, reason] of matches) {
		const root = find(index);
		if (!reasonsByRoot.has(root))
			reasonsByRoot.set(root, new Set());
		reasonsByRoot.get(root)!.add(reason);
	}

	const membersByRoot = new Map<number, Candidate[]>();
	candidates.forEach((candidate, index) => {
		const root = find(index);
		if (!reasonsByRoot.has(root))
			return;
		if (!membersByRoot.has(root))
			membersByRoot.set(root, []);
		membersByRoot.get(root)!.push(candidate);
	});

	return Array.from(membersByRoot.entries())
		.map(([root, members]) => {
			const sorted = [...members].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
			return {
				key: sorted.map(candidate => candidate.id).join("|"),
				candidates: sorted,
				reasons: Array.from(reasonsByRoot.get(root)!),
			};
		})
		.sort((a, b) =>
			b.candidates.length - a.candidates.length
			|| (a.candidates[0].name || "").localeCompare(b.candidates[0].name || "")
		);
}
